import React, { useState } from 'react'
import { Star, Quote, Languages } from 'lucide-react'

const testimonials = [
  {
    name: 'Jeffrey Soriano',
    role: 'Suds & Bubbles',
    location: 'Mandaluyong',
    image: '👨',
    rating: 5,
    metric: '+₱18K/month',
    textTagalog: 'Nakita ko lahat ng leaks sa business ko. Revenue tumaas ng ₱18,000 per month!',
    textEnglish: 'I saw all the leaks in my business. Revenue increased by ₱18,000 per month!'
  },
  {
    name: 'Ernesto Bautista Jr.',
    role: 'Bubble Magic Laundry',
    location: 'Quezon City',
    image: '👨‍💼',
    rating: 5,
    metric: '3hrs → 25min/day',
    textTagalog: 'Dati 3 hours ang tracking, ngayon 25 minutes lang. Nag-add na ko ng delivery service!',
    textEnglish: 'Used to spend 3 hours tracking, now just 25 minutes. I added delivery service!'
  },
  {
    name: 'Alvin Macapagal',
    role: 'Wash & Dry Express',
    location: 'Taguig',
    image: '👨‍🔧',
    rating: 5,
    metric: '2x orders/week',
    textTagalog: 'From 20-25 orders per week, umabot na ng 45+. Sulit na sulit!',
    textEnglish: 'From 20-25 orders per week, it reached 45+. Totally worth it!'
  }
]

function TestimonialCard({ testimonial, index }) {
  const [isEnglish, setIsEnglish] = useState(false)

  return (
    <div
      className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 relative"
    >
      {/* Quote Icon */}
      <div className="absolute top-4 right-4 text-cyan-200 dark:text-cyan-900/30">
        <Quote className="w-10 h-10" />
      </div>

      {/* Rating */}
      <div className="flex gap-1 mb-3">
        {[...Array(testimonial.rating)].map((_, i) => (
          <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
        ))}
      </div>

      {/* Metrics Badge - Prominent */}
      <div className="mb-4 inline-block px-4 py-2 bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900/40 dark:to-emerald-900/40 rounded-xl">
        <p className="text-lg font-bold text-green-700 dark:text-green-400">
          📈 {testimonial.metric}
        </p>
      </div>

      {/* Testimonial Text */}
      <p className="text-gray-700 dark:text-gray-300 mb-4 leading-relaxed relative z-10 text-base">
        "{isEnglish ? testimonial.textEnglish : testimonial.textTagalog}"
      </p>

      {/* Language Toggle */}
      <button
        onClick={() => setIsEnglish(!isEnglish)}
        className="inline-flex items-center gap-2 px-3 py-1.5 mb-4 text-xs font-medium text-cyan-600 dark:text-cyan-400 bg-cyan-50 dark:bg-cyan-900/30 hover:bg-cyan-100 dark:hover:bg-cyan-900/50 rounded-lg transition-colors duration-200"
      >
        <Languages className="w-3.5 h-3.5" />
        {isEnglish ? 'Tagalog' : 'English'}
      </button>

      {/* Author Info */}
      <div className="flex items-center gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-400 to-cyan-600 flex items-center justify-center text-2xl flex-shrink-0">
          {testimonial.image}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-bold text-gray-900 dark:text-white text-sm truncate">{testimonial.name}</h4>
          <p className="text-xs text-gray-600 dark:text-gray-400 truncate">{testimonial.role}</p>
          <p className="text-xs text-gray-500 dark:text-gray-500">{testimonial.location}</p>
        </div>
      </div>
    </div>
  )
}

export default function Testimonials() {
  return (
    <section id="testimonials" className="py-20 bg-gradient-to-br from-gray-50 to-cyan-50 dark:from-gray-900 dark:to-gray-800 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Loved by
            <span className="gradient-text"> Shop Owners</span>
          </h2>
          <p className="text-lg sm:text-xl text-gray-700 dark:text-gray-300 max-w-3xl mx-auto">
            Real stories from real laundry shop owners using LaundryMate
          </p>
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {testimonials.map((testimonial, index) => (
            <TestimonialCard key={index} testimonial={testimonial} index={index} />
          ))}
        </div>

        {/* Stats Section */}
        <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8">
          {[
            { number: '50+', label: 'Active Shops' },
            { number: '10K+', label: 'Orders Processed' },
            { number: '98%', label: 'Satisfaction Rate' },
            { number: '24/7', label: 'Support Available' }
          ].map((stat, index) => (
            <div key={index} className="text-center">
              <div className="text-4xl md:text-5xl font-bold gradient-text mb-2">
                {stat.number}
              </div>
              <div className="text-gray-600 font-medium">
                {stat.label}
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <p className="text-gray-900 dark:text-white mb-6 text-lg">
            Join these successful shop owners today
          </p>
          <a
            href="https://app.laundrymate.shop"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105 active:scale-95"
          >
            Get Started Now
          </a>
        </div>
      </div>
    </section>
  )
}
